import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useServices } from "@/hooks/useSiteContent";
import ContentStatus from "@/components/feature/ContentStatus";

export default function ServicesSection() {
  const { data: services, loading, error, reload } = useServices();
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const formatPrice = (price: number) =>
    new Intl.NumberFormat("vi-VN").format(price);

  return (
    <section
      ref={sectionRef}
      className="w-full bg-background-50 py-14 md:py-20"
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div
          className={`text-center mb-10 md:mb-14 transition-all duration-700 ${
            visible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-6"
          }`}
        >
          <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-primary-100 text-primary-700 text-xs md:text-sm font-label font-medium rounded-full mb-4">
            <i className="ri-sparkling-2-line" />
            Dịch vụ của chúng tôi
          </span>
          <h2 className="font-display text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Công nghệ chăm sóc sức khỏe hiện đại
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto font-body">
            Trải nghiệm công nghệ cao giúp bạn giảm cân, cải thiện vóc dáng và thư giãn cơ thể một cách an toàn, hiệu quả.
          </p>
        </div>

        {/* Service Cards */}
        <ContentStatus loading={loading} error={error} onRetry={reload}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {services.map((service, index) => (
            <div
              key={service.id}
              className={`group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-500 ${
                visible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
              style={{
                transitionDelay: visible ? `${index * 120}ms` : "0ms",
              }}
            >
              {/* Image */}
              <div className="relative h-48 md:h-52 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3">
                  <span className="px-2.5 py-1 bg-accent-500 text-background-50 text-xs font-label font-semibold rounded-md whitespace-nowrap">
                    {service.duration}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 md:p-5">
                <h3 className="font-display text-base md:text-lg font-semibold text-foreground-950 mb-2 group-hover:text-primary-700 transition-colors">
                  {service.shortName}
                </h3>
                <p className="text-xs md:text-sm text-foreground-600 mb-4 line-clamp-2 font-body leading-relaxed">
                  {service.description}
                </p>

                {/* Price */}
                <div className="flex items-end gap-2 mb-4">
                  <span className="font-display text-lg md:text-xl font-bold text-primary-600">
                    {formatPrice(service.promoPrice)}đ
                  </span>
                  <span className="text-xs md:text-sm text-foreground-400 line-through font-body">
                    {formatPrice(service.price)}đ
                  </span>
                </div>

                <Link
                  to={`/service/${service.slug}`}
                  className="flex items-center gap-1.5 text-sm font-label font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  Tìm hiểu thêm
                  <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          ))}
        </div>
        </ContentStatus>

        {/* CTA */}
        <div
          className={`text-center mt-10 md:mt-14 transition-all duration-700 delay-500 ${
            visible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-4"
          }`}
        >
          <Link
            to="/services"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors"
          >
            Xem tất cả dịch vụ
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}